import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const {
    t
  } = useLanguage();
  const isActive = (path: string) => location.pathname === path;
  const isActiveSection = (paths: string[]) => paths.some(path => location.pathname.startsWith(path));
  const toggleDropdown = (menu: string) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);
  return <nav ref={navRef} className="fixed top-0 left-0 right-0 z-[9999] bg-slate-950/80 backdrop-blur-xl border-b border-cyan-500/20 shadow-lg transition-all duration-300" data-id="element-394">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" data-id="element-395"></div>

      <div className="container mx-auto px-4" data-id="element-396">
        <div className="flex items-center justify-between h-14" data-id="element-397">
          <Link to="/" className="flex items-center space-x-2 group" data-id="element-398">
            <div className="relative" data-id="element-399">
              <Shield className="h-7 w-7 text-cyan-500 group-hover:drop-shadow-[0_0_8px_rgba(6,182,212,0.8)] transition-all" data-id="element-400" />
              <div className="absolute inset-0 bg-cyan-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-401"></div>
            </div>
            <span className="text-lg font-bold text-slate-100 font-mono" data-id="element-402">
              CyberSecurity<span className="text-cyan-500" data-id="element-403">Protocol</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center space-x-1" data-id="element-404">
            <NavLink to="/start" active={isActive('/start')} data-id="element-405">
              {t('nav.startHere')}
            </NavLink>

            <DropdownMenu label={t('nav.about')} isOpen={openDropdown === 'about'} onToggle={() => toggleDropdown('about')} isActiveSection={isActiveSection(['/about'])} align="left" data-id="element-406">
              <DropdownLink to="/about/mission" data-id="element-407">
                {t('nav.mission')}
              </DropdownLink>
              <DropdownLink to="/about/scope" data-id="element-408">{t('nav.scope')}</DropdownLink>
              <DropdownLink to="/about/standards" data-id="element-409">
                {t('nav.standards')}
              </DropdownLink>
              <DropdownLink to="/about/changelog" data-id="element-410">
                {t('nav.changelog')}
              </DropdownLink>
              <DropdownLink to="/about/governance" data-id="element-411">
                {t('nav.governance')}
              </DropdownLink>
              <DropdownLink to="/about/disclaimer" data-id="element-412">
                {t('nav.disclaimer')}
              </DropdownLink>
              <DropdownLink to="/about/contact" data-id="element-413">
                {t('nav.contact')}
              </DropdownLink>
            </DropdownMenu>

            <DropdownMenu label={t('nav.protocols')} isOpen={openDropdown === 'protocols'} onToggle={() => toggleDropdown('protocols')} isActiveSection={isActiveSection(['/personal', '/organization'])} align="left" data-id="element-414">
              <DropdownLink to="/personal" data-id="element-415">{t('nav.personal')}</DropdownLink>
              <DropdownLink to="/organization" data-id="element-416">
                {t('nav.organization')}
              </DropdownLink>
            </DropdownMenu>

            <DropdownMenu label={t('nav.tools')} isOpen={openDropdown === 'tools'} onToggle={() => toggleDropdown('tools')} isActiveSection={isActiveSection(['/tools'])} align="right" data-id="element-417">
              <DropdownLink to="/tools/breach-check" data-id="element-418">Breach Check</DropdownLink>
              <DropdownLink to="/tools/password-safety" data-id="element-419">
                Password Safety
              </DropdownLink>
              <DropdownLink to="/tools/mfa-passkeys" data-id="element-420">
                MFA & Passkeys
              </DropdownLink>
              <DropdownLink to="/tools/privacy" data-id="element-421">Privacy Tools</DropdownLink>
              <DropdownLink to="/tools/backup" data-id="element-422">Backup Tools</DropdownLink>
              <DropdownLink to="/tools/business-security" data-id="element-423">
                Business Security
              </DropdownLink>
            </DropdownMenu>

            <DropdownMenu label={t('nav.compliance')} isOpen={openDropdown === 'compliance'} onToggle={() => toggleDropdown('compliance')} isActiveSection={isActiveSection(['/compliance'])} align="right" data-id="element-424">
              <DropdownLink to="/compliance/global" data-id="element-425">
                Global Standards
              </DropdownLink>
              <DropdownLink to="/compliance/indonesia" data-id="element-426">
                Indonesia (UU PDP)
              </DropdownLink>
            </DropdownMenu>

            <LanguageSwitcher data-id="element-427" />
          </div>

          <div className="flex items-center space-x-2 lg:hidden" data-id="element-428">
            <LanguageSwitcher data-id="element-429" />
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-lg hover:bg-cyan-500/10 transition-colors border border-cyan-500/20" aria-label="Toggle menu" data-id="element-430">
              {isOpen ? <X className="h-6 w-6 text-cyan-400" data-id="element-431" /> : <Menu className="h-6 w-6 text-cyan-400" data-id="element-432" />}
            </button>
          </div>
        </div>

        {isOpen && <div className="lg:hidden py-4 border-t border-cyan-500/10 max-h-[calc(100vh-4rem)] overflow-y-auto" data-id="element-433">
            <div className="space-y-2" data-id="element-434">
              <MobileNavLink to="/start" onClick={() => setIsOpen(false)} data-id="element-435">
                {t('nav.startHere')}
              </MobileNavLink>

              <MobileSection title={t('nav.about')} data-id="element-436">
                <MobileNavLink to="/about/mission" onClick={() => setIsOpen(false)} data-id="element-437">
                  {t('nav.mission')}
                </MobileNavLink>
                <MobileNavLink to="/about/scope" onClick={() => setIsOpen(false)} data-id="element-438">
                  {t('nav.scope')}
                </MobileNavLink>
                <MobileNavLink to="/about/standards" onClick={() => setIsOpen(false)} data-id="element-439">
                  {t('nav.standards')}
                </MobileNavLink>
                <MobileNavLink to="/about/changelog" onClick={() => setIsOpen(false)} data-id="element-440">
                  {t('nav.changelog')}
                </MobileNavLink>
                <MobileNavLink to="/about/governance" onClick={() => setIsOpen(false)} data-id="element-441">
                  {t('nav.governance')}
                </MobileNavLink>
                <MobileNavLink to="/about/disclaimer" onClick={() => setIsOpen(false)} data-id="element-442">
                  {t('nav.disclaimer')}
                </MobileNavLink>
                <MobileNavLink to="/about/contact" onClick={() => setIsOpen(false)} data-id="element-443">
                  {t('nav.contact')}
                </MobileNavLink>
              </MobileSection>

              <MobileSection title={t('nav.protocols')} data-id="element-444">
                <MobileNavLink to="/personal" onClick={() => setIsOpen(false)} data-id="element-445">
                  {t('nav.personal')}
                </MobileNavLink>
                <MobileNavLink to="/organization" onClick={() => setIsOpen(false)} data-id="element-446">
                  {t('nav.organization')}
                </MobileNavLink>
              </MobileSection>

              <MobileSection title={t('nav.tools')} data-id="element-447">
                <MobileNavLink to="/tools/breach-check" onClick={() => setIsOpen(false)} data-id="element-448">
                  Breach Check
                </MobileNavLink>
                <MobileNavLink to="/tools/password-safety" onClick={() => setIsOpen(false)} data-id="element-449">
                  Password Safety
                </MobileNavLink>
                <MobileNavLink to="/tools/mfa-passkeys" onClick={() => setIsOpen(false)} data-id="element-450">
                  MFA & Passkeys
                </MobileNavLink>
                <MobileNavLink to="/tools/privacy" onClick={() => setIsOpen(false)} data-id="element-451">
                  Privacy Tools
                </MobileNavLink>
                <MobileNavLink to="/tools/backup" onClick={() => setIsOpen(false)} data-id="element-452">
                  Backup Tools
                </MobileNavLink>
                <MobileNavLink to="/tools/business-security" onClick={() => setIsOpen(false)} data-id="element-453">
                  Business Security
                </MobileNavLink>
              </MobileSection>

              <MobileSection title={t('nav.compliance')} data-id="element-454">
                <MobileNavLink to="/compliance/global" onClick={() => setIsOpen(false)} data-id="element-455">
                  Global Standards
                </MobileNavLink>
                <MobileNavLink to="/compliance/indonesia" onClick={() => setIsOpen(false)} data-id="element-456">
                  Indonesia (UU PDP)
                </MobileNavLink>
              </MobileSection>
            </div>
          </div>}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" data-id="element-457"></div>
    </nav>;
}
function NavLink({
  to,
  active,
  children
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return <Link to={to} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all font-mono relative group ${active ? 'bg-cyan-500/10 text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.3)]' : 'text-slate-300 hover:bg-cyan-500/5 hover:text-cyan-400'}`} data-id="element-458">
      {children}
      {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" data-id="element-459"></div>}
    </Link>;
}
function DropdownMenu({
  label,
  isOpen,
  onToggle,
  isActiveSection,
  align = 'left',
  children
}: {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  isActiveSection: boolean;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const alignmentClass = align === 'right' ? 'right-0' : 'left-0';
  return <div className="relative" data-id="element-460">
      <button onClick={onToggle} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-1 font-mono ${isActiveSection ? 'bg-cyan-500/10 text-cyan-400' : 'text-slate-300 hover:bg-cyan-500/5 hover:text-cyan-400'}`} data-id="element-461">
        <span data-id="element-462">{label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-463" />
      </button>

      {isOpen && <div className={`absolute top-full ${alignmentClass} mt-2 w-56 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-xl shadow-2xl py-2 z-[10000]`} data-id="element-464">
          {children}
        </div>}
    </div>;
}
function DropdownLink({
  to,
  children
}: {
  to: string;
  children: React.ReactNode;
}) {
  return <Link to={to} className="block px-4 py-2 text-sm text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-400 transition-colors font-mono" data-id="element-465">
      {children}
    </Link>;
}
function MobileNavLink({
  to,
  onClick,
  children
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <Link to={to} onClick={onClick} className="block px-4 py-2 text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-400 rounded-lg transition-colors font-mono" data-id="element-466">
      {children}
    </Link>;
}
function MobileSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="py-2" data-id="element-467">
      <div className="px-4 py-2 text-xs font-bold text-cyan-500 uppercase font-mono" data-id="element-468">
        {title}
      </div>
      <div className="space-y-1" data-id="element-469">{children}</div>
    </div>;
}